import type { UserFoodDocType } from '../validators';
import type { Assert, AssertEqual } from './type-assertions';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';
import { defineRxdbCollectionModel } from './migration';

// ============================================================================
// RxDB collection schema for userFoods (custom foods).
//
// Document shape mirrors the D1 `user_foods` row (flat fields, no JSON body).
// `isSharedByOwner` is a server-computed field added at pull time — not stored
// in D1 but included in the doc so the client can surface sharing state.
// ============================================================================

export const USER_FOODS_COLLECTION = 'userFoods' as const;
export const USER_FOODS_SCHEMA_VERSION = 0;

export const userFoodsSchemaLiteral = {
  title: 'user foods schema',
  version: USER_FOODS_SCHEMA_VERSION,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    userId: { type: 'string', maxLength: 100 },
    name: { type: 'string', maxLength: 500 },
    brand: { type: ['string', 'null'] },
    upc: { type: ['string', 'null'] },
    calories: { type: 'number' },
    protein: { type: 'number' },
    carbs: { type: 'number' },
    fat: { type: 'number' },
    servingSize: { type: ['number', 'null'] },
    servingUnit: { type: 'string', maxLength: 100 },
    isFavorite: { type: 'boolean' },
    isArchived: { type: 'boolean' },
    isProvisional: { type: 'boolean' },
    isSharedByOwner: { type: 'boolean' },
    createdAt: { type: 'string', maxLength: 30 },
    updatedAt: { type: 'string', maxLength: 30 },
  },
  required: ['id', 'userId', 'name', 'brand', 'upc', 'calories', 'protein', 'carbs', 'fat', 'servingSize', 'servingUnit', 'isFavorite', 'isArchived', 'isProvisional', 'isSharedByOwner', 'createdAt', 'updatedAt'],
  indexes: [['userId', 'updatedAt']],
} as const;

export interface UserFoodsCheckpoint {
  updatedAt: string;
  id: string;
}

type _UserFoodSchemaDocType = ExtractDocumentTypeFromTypedRxJsonSchema<typeof userFoodsSchemaLiteral>;
type _UserFoodDocTypeInSync = Assert<AssertEqual<_UserFoodSchemaDocType, UserFoodDocType>>;

export const userFoodsSchema: RxJsonSchema<UserFoodDocType> = userFoodsSchemaLiteral;
export const userFoodsModel = defineRxdbCollectionModel<typeof USER_FOODS_COLLECTION, UserFoodDocType>({
  collection: USER_FOODS_COLLECTION,
  schema: userFoodsSchema,
});
